import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import ParentContainor from '../../components/base/ParentContainor';
import LeaderboardScreen from '../../components/LeaderboardScreen';
import TournamentProgressStepper, {
  DEFAULT_STAGES,
} from '../../components/TournamentProgressStepper';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { getTournamentLeaderboard } from '../../network/network_callbacks';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { PAGE_OUTER_PADDING } from '../../constants/housieLiveStyles';
import {
  extractLeaderboardPlayers,
  extractTournamentStages,
  getPlayerPoints,
  mergeTournamentPrizes,
  normalizeLeaderboardPlayer,
} from '../../utils/utils';

const rankLeaderboardPlayers = (players = []) => {
  const normalized = players.map((user, index) =>
    normalizeLeaderboardPlayer(user, index)
  );
  const hasApiRank = normalized.some((player) => Number(player.rank) > 0);
  if (hasApiRank) {
    return [...normalized].sort(
      (a, b) => Number(a.rank || 0) - Number(b.rank || 0)
    );
  }
  return [...normalized]
    .sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a))
    .map((user, index) => ({
      ...user,
      rank: index + 1,
    }));
};

const TournamentLeaderBoard = () => {
  const location = useLocation();
  const { tournamentId: tournamentIdFromParams } = useParams();
  const [searchParams] = useSearchParams();
  const { tournamentId: tournamentIdFromState, contestId } = location.state || {};
  const tournamentId =
    tournamentIdFromParams ||
    tournamentIdFromState ||
    contestId ||
    searchParams.get('tournamentId') ||
    searchParams.get('id') ||
    undefined;

  const canvasRef = React.useRef(null);
  const [showBoard, setShowBoard] = useState(false);
  const [leaderBoardData, setLeaderBoardData] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [tournamentMeta, setTournamentMeta] = useState({
    name: '',
    progressTitle: TEXT_LABELS.TOURNAMENT_PROGRESS_TITLE,
    progressSubtitle: TEXT_LABELS.TOURNAMENT_PROGRESS_SUBTITLE,
    leaderboardTitle: TEXT_LABELS.TOURNAMENT_LEADERBOARD_WINNER_WISHES,
    rewardBanners: [],
    rewardStatus: false,
    prizeAllotments: [],
    prizeWinners: [],
    prizeHeading: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_EYEBROW,
    prizeTitle: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE,
    prizeSubtitle: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_SUBTITLE,
  });

  useEffect(() => {
    getTournamentLeaderBoardApiData();
    return () => {
      setShowBoard(false);
      setLeaderBoardData([]);
      setStages(DEFAULT_STAGES);
      setTournamentMeta({
        name: '',
        progressTitle: TEXT_LABELS.TOURNAMENT_PROGRESS_TITLE,
        progressSubtitle: TEXT_LABELS.TOURNAMENT_PROGRESS_SUBTITLE,
        leaderboardTitle: TEXT_LABELS.TOURNAMENT_LEADERBOARD_WINNER_WISHES,
        rewardBanners: [],
        rewardStatus: false,
        prizeAllotments: [],
        prizeWinners: [],
        prizeHeading: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_EYEBROW,
        prizeTitle: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE,
        prizeSubtitle: TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_SUBTITLE,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const getTournamentLeaderBoardApiData = async () => {
    if (!tournamentId) {
      setShowBoard(true);
      return;
    }

    try {
      const response = await getTournamentLeaderboard(tournamentId);
      const payload = response?.data;
      const ok = payload?.responseCode === 200 || response?.status === 200;
      const data = payload?.data ?? payload;
      if (ok && data) {
        const participants = mergeTournamentPrizes(
          extractLeaderboardPlayers(data),
          data
        );
        setLeaderBoardData(participants);

        const nextStages = extractTournamentStages(data);
        if (nextStages.length > 0) {
          setStages(nextStages);
        }

        const rewardBanners = (
          Array.isArray(data?.tournamentRewardImagesArray)
            ? [...data.tournamentRewardImagesArray]
            : []
        )
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((item, index) => ({
            id: `reward-${item.order ?? index}`,
            src: item.image,
            order: item.order ?? index + 1,
            alt: `Tournament reward ${item.order ?? index + 1}`,
          }))
          .filter((item) => item.src);

        setTournamentMeta({
          name: data.tournamentName || '',
          progressTitle:
            data.progressTitle || TEXT_LABELS.TOURNAMENT_PROGRESS_TITLE,
          progressSubtitle:
            data.progressSubtitle || TEXT_LABELS.TOURNAMENT_PROGRESS_SUBTITLE,
          leaderboardTitle:
            data.leaderboardTitle ||
            payload?.leaderboardTitle ||
            TEXT_LABELS.TOURNAMENT_LEADERBOARD_WINNER_WISHES,
          rewardBanners,
          rewardStatus: Boolean(data.rewardStatus),
          prizeAllotments: Array.isArray(data.tournamentRewardAllotmentArray)
            ? data.tournamentRewardAllotmentArray
            : Array.isArray(data.prizeTabs)
              ? data.prizeTabs
              : [],
          prizeWinners: Array.isArray(data.winners) ? data.winners : [],
          prizeHeading:
            data.winnerSection?.heading ||
            TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_EYEBROW,
          prizeTitle:
            data.winnerSection?.title ||
            TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_TITLE,
          prizeSubtitle:
            data.winnerSection?.subtitle ||
            TEXT_LABELS.TOURNAMENT_WINNER_PRIZES_SUBTITLE,
        });

        const winners = rankLeaderboardPlayers(participants)
          .filter((p) => getPlayerPoints(p) > 0)
          .slice(0, 3);

        if (participants.length > 0 && winners.length > 0) {
          if (canvasRef.current) {
            const myConfetti = confetti.create(canvasRef.current, {
              resize: true,
              useWorker: true,
            });
            myConfetti({
              startVelocity: 80,
              particleCount: 800,
              spread: 360,
              ticks: 10000,
              colors: ['#FF00F2', '#8800D1', '#FFFFFF', '#FF60AD', '#C000B0'],
            });
            setTimeout(() => setShowBoard(true), 2200);
          } else {
            setShowBoard(true);
          }
        } else {
          setShowBoard(true);
        }
      } else {
        setShowBoard(true);
      }
    } catch (error) {
      console.error('❌ Failed to getTournamentLeaderBoardApiData : ', error);
      setShowBoard(true);
    }
  };

  const rankedUsers = useMemo(
    () => rankLeaderboardPlayers(leaderBoardData),
    [leaderBoardData]
  );

  const hasWinners = rankedUsers.some((player) => getPlayerPoints(player) > 0);

  return (
    <ParentContainor
      route={ROUTE_ENUM.TOURNAMENT_LEADERBOARD}
      parentContainorStyle={{ justifyContent: 'flex-start' }}
      isShowHeader={false}
      showCopyRightText
      isBreadcrumbVisible={false}
      padding={PAGE_OUTER_PADDING}
      isShowRapFireLeaderboardSpotlight={
        !showBoard && hasWinners && leaderBoardData?.length > 0
      }
      showBlurredBg
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 999,
        }}
      />

      <LeaderboardScreen
        headerTitle={tournamentMeta.name || ''}
        hideBack
        variant="tournament"
        headerExtra={
          <TournamentProgressStepper
            title={tournamentMeta.progressTitle}
            subtitle={tournamentMeta.progressSubtitle}
            stages={stages}
          />
        }
        columns={{
          rank: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_RANK,
          player: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_PLAYER,
          points: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_GAME_POINTS,
          won: TEXT_LABELS.TOURNAMENT_LEADERBOARD_COL_TOTAL_POINTS,
        }}
        players={rankedUsers}
        showBoard={showBoard}
        hasWinners={hasWinners}
        emptyLabel={TEXT_LABELS.TOURNAMENT_LEADERBOARD_NO_WINNERS}
        congratsLabel={
          hasWinners
            ? tournamentMeta.leaderboardTitle
            : TEXT_LABELS.TOURNAMENT_LEADERBOARD_NO_WINNERS
        }
        promoBanners={tournamentMeta.rewardBanners}
        rewardStatus={tournamentMeta.rewardStatus}
        prizeAllotments={tournamentMeta.prizeAllotments}
        prizeWinners={tournamentMeta.prizeWinners}
        prizeHeading={tournamentMeta.prizeHeading}
        prizeTitle={tournamentMeta.prizeTitle}
        prizeSubtitle={tournamentMeta.prizeSubtitle}
      />
    </ParentContainor>
  );
};

export default TournamentLeaderBoard;
